/* eslint-disable no-async-promise-executor */
import axios from 'axios';
import Settings from '@/plugins/appSettings';
import store from '@/store';
import handleError from '@/_core/helpers/errorHandler';
import { encryptPayload, decryptPayload, isEncryptionEnabled } from '@/_core/util/encryption';

const refreshToken = () => {
  const refreshToken = localStorage.getItem('auth')
    ? JSON.parse(localStorage.getItem('auth')).refreshToken
    : '';

  return Api()
    .post('oauth/refresh-token', {
      token: refreshToken,
    })
    .then(({ data }) => data)
    .then(({ token, id }) => {
      if (id) {
        localStorage.setItem('userUniqueId', id);
      }
      return token;
    })
    .catch((e) => console.log(e));
};

const authToken = ({ state, code }) => {
  if (!state && !code) {
    return Api()
      .get('oauth/authorization-uri')
      .then(({ data }) => {
        if (data) {
          const redirectURL = store.getters['userStore/userData'].redirectURL;
          const { uri } = data;
          const redirectUri = uri.includes('scope=openId')
            ? uri.replace('scope=openId', `redirect_uri=${redirectURL}`)
            : uri + `&redirect_uri=${redirectURL}`;

          window.location = redirectUri;
        }
      })
      .catch((e) => console.log(e));
  }
  if (state && code) {
    if (localStorage.getItem('auth')) {
      return;
    }

    const redirectUri = store.getters['userStore/userData'].redirectURL;

    return Api()
      .post('oauth/validate-oauth-code', {
        code,
        redirectUri,
      })
      .then(({ data }) => data)
      .then(({ token, id }) => {
        if (id) {
          localStorage.setItem('userUniqueId', id);
        }
        return token;
      })
      .catch((e) => {
        setTimeout(() => {
          handleError(e, undefined, true);
        }, 1000);
      });
  }
};

const getTenantDomain = () => {
  if (store.getters['appStore/appData'].tenantDomain) {
    return store.getters['appStore/appData'].tenantDomain;
  }
  const { tenantDomainAttribute } = Settings.defaultConfigurations;
  const hostname = window.location.hostname;
  const tenant = hostname.split('.');
  const tenantDomain = tenantDomainAttribute === 'subdomain' ? tenant[0] : hostname;

  store.dispatch('appStore/setAppData', {
    tenantDomain,
  });
  return tenantDomain;
};

const getLocale = () => {
  return (
    (store.getters['userStore/userData'].userDetails &&
    store.getters['userStore/userData'].userDetails.userLanguageCode) || Settings.defaultConfigurations.language
  );
};

const getBranch = () => {
  return (
    store.getters['userStore/userData'].userDetails &&
    store.getters['userStore/userData'].userDetails.branchCode
  );
};

const getUser = () => {
  if (store.getters['userStore/userData'].permissions?.userPermissions) {
    return store.getters['userStore/userData'].permissions.userPermissions.userLoginId;
  }
};

const Api = (headerObj = {}) => {
  let accessToken = '';
  if (store.getters['userStore/userData'].isRefreshToken) {
    accessToken = store.getters['userStore/userData']?.auth
      ? store.getters['userStore/userData'].auth.accessToken
      : '';
  } else {
    accessToken = localStorage.getItem('auth')
      ? JSON.parse(localStorage.getItem('auth')).accessToken
      : '';
  }
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    TenantDomain: getTenantDomain(),
    'x-language-code': getLocale(),
    locale: getLocale(),
    'X-Requested-With': 'XMLHttpRequest',
  };
  headerObj = getUser() ? Object.assign(headerObj, { 'x-branch-code': getBranch(), userLoginId: getUser() }) : headerObj;
  Object.assign(headers, headerObj);

  const axoisApi = axios.create({
    baseURL: Settings.serverSettings.baseUrl,
    withCredentials: false,
    headers,
  });

  axoisApi.interceptors.request.use((config) => {
    // Below condition is added to ignore loader for en_GB & Redirect URL API calls as it was fetching cached values
    if (!(['/locale/en_GB', '/getRedirectURL'].includes(config.url)) && !config.url.includes('review')) {
      store.dispatch('appStore/setAppData', {
        inProgress: true,
      });
    }

    // Payload encryption - add header if enabled (for all requests)
    if (isEncryptionEnabled()) {
      config.headers['X-Payload-Encrypted'] = 'true';

      // Encrypt request data for POST/PUT/PATCH methods
      if (config.data && config.method !== 'get') {
        const encryptedData = encryptPayload(config.data);
        config.data = {
          data: encryptedData,
        };

        if (process.env.NODE_ENV !== 'production') {
          console.log('[Payload Encryption] Request encrypted');
        }
      }
    }

    return config;
  });

  axoisApi.interceptors.response.use(
    (response) => {
      store.dispatch('appStore/setAppData', {
        inProgress: false,
      });

      // Payload decryption - decrypt response data if enabled
      const isEncryptedResponse =
        isEncryptionEnabled() &&
        response.data &&
        typeof response.data === 'object' &&
        typeof response.data.data === 'string' &&
        (response.data.encrypted === true || Object.keys(response.data).length === 1);

      if (isEncryptedResponse) {
        const decryptedData = decryptPayload(response.data.data);
        response.data = decryptedData;

        if (process.env.NODE_ENV !== 'production') {
          console.log('[Payload Encryption] Response decrypted');
        }
      }

      return response;
    },
    async(error) => {
      // Payload decryption - decrypt error response data if enabled
      if (
        isEncryptionEnabled() &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        typeof error.response.data.data === 'string' &&
        (error.response.data.encrypted === true || Object.keys(error.response.data).length === 1)
      ) {
        error.response.data = decryptPayload(error.response.data.data);

        if (process.env.NODE_ENV !== 'production') {
          console.log('[Payload Encryption] Error response decrypted');
        }
      }

      const {
        config,
        response: { status },
      } = error;
      const originalRequest = config;
      const ErrorCodes = [401];

      if (ErrorCodes.includes(status) && localStorage.getItem('auth')) {
        const retryOriginalRequest = new Promise(async(resolve) => {
          const tokenSetTime = store.getters['userStore/userData']?.tokenSetTime || 0;
          const refreshTime = Math.abs(new Date(Date.now()) - tokenSetTime);

          if (
            (Math.floor(refreshTime / 1000) <
              JSON.parse(localStorage.getItem('auth')).refreshExpiresIn)
          ) {
            const tokenResponse = await refreshToken();

            if (tokenResponse) {
              store.dispatch('userStore/setUserData', {
                token: tokenResponse,
                tokenSetTime: new Date(Date.now()),
              });
              localStorage.setItem('auth', JSON.stringify(tokenResponse));

              const accessToken = tokenResponse.accessToken;

              if (originalRequest) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                resolve(axios(originalRequest));
              }
            }
          } else {
            localStorage.removeItem('auth');
            const urlParams = new URLSearchParams(window.location.search);
            const state = urlParams.get('state');
            const code = urlParams.get('code');
            const tokenResponse = await authToken({ state, code });

            if (tokenResponse) {
              store.dispatch('userStore/setUserData', {
                token: tokenResponse,
                tokenSetTime: new Date(Date.now()),
              });
              localStorage.setItem('auth', JSON.stringify(tokenResponse));

              const accessToken = tokenResponse.accessToken;
              if (originalRequest) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                resolve(axios(originalRequest));
              }
            }
          }
        });

        return retryOriginalRequest;
      }
      if (ErrorCodes.includes(status) && !localStorage.getItem('auth')) {
        const retryOriginalRequest = new Promise(async(resolve) => {
          const urlParams = new URLSearchParams(window.location.search);
          const state = urlParams.get('state');
          const code = urlParams.get('code');
          const tokenResponse = await authToken({ state, code });

          if (tokenResponse) {
            store.dispatch('userStore/setUserData', {
              token: tokenResponse,
              tokenSetTime: new Date(Date.now()),
            });
            localStorage.setItem('auth', JSON.stringify(tokenResponse));
            const accessToken = tokenResponse.accessToken;

            if (originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              resolve(axios(originalRequest));
            }
          }
        });

        return retryOriginalRequest;
      }

      return Promise.reject(error);
    },
  );

  return axoisApi;
};

export { Api };
